/** @module Camunda/_TaskSetupMixin */
define([
	"dojo/_base/declare",
	"underscore",
	"csi/action/ActionView",
	"csi/action/Popup",
	"csi/action/PopupController",
	"csi/core/extendedPathService",
	"csi/def/Toolbar",
	"csi/def/ViewStoreBuilder",
	"csi/html",
	"csi/locale",
	"csi/xml"
], function(declare, _, ActionView, Popup, PopupController, {parseExtendedPath}, Toolbar, ViewStoreBuilder, html, locale, xml) {

	const ACTION_EXAMPLE = "CamundaTaskSetupPopupExample";

	const _ExampleController = declare(PopupController, {
		/** @property {string} _exampleTitle - title for popup view */
		_exampleTitle: null,

		/**
		 * @override
		 * Create ActionView based on selected view
		 */
		getActionView: function() {
			const task = this.getNode();
			const viewStoreId = task.ViewStoreId;
			const viewPath = parseExtendedPath(task.ViewDefinitionPath);
			const defView = ViewStoreBuilder.getViewDefinition(viewStoreId, viewPath);

			// omit toolbar from preview
			defView.defToolbar = _.extend(new Toolbar(), {plugins: ''});

			// store title for dialog
			this._exampleTitle = locale.type(viewPath.getStartType()) + ": " + locale.typeView(viewPath.getEndType(), defView.getId());

			return new ActionView(this, defView);
		},

		/**
		 * @override
		 * Return title for popup view
		 */
		getTitle: function() {
			return this._exampleTitle;
		},

		/**
		 * @override
		 * Specify custom view for popup view and disable ability to change it
		 */
		onCreatePopupParams: function(params) {
			params.disableCustomViewToolbar = true;
			params.urlDefaultView = this.getNode().CustomView;
		},

		/**
		 * @override
		 * Don't allow editing the target view
		 */
		onFormData: function(fd) {
			fd.m_bAllowSelections = false;
			fd.m_bEditable = false;
		},

		/**
		 * @override
		 * If an error was encountered, then close popup and show error message
		 */
		onLoadPopup: function(view) {
			if (view.errorOnLoad) {
				this.closeDialog();
				this.displayErrorDialog();
			} else {
				this.inherited(arguments);
			}
		}
	});

	return declare(null, {
		/**
		 * Register action to open example view
		 */
		_addTaskSetupExampleAction: function() {
			this.defView._addViewAction(new Popup(), {
				m_class: "csi-camunda-example-action",
				m_id: ACTION_EXAMPLE,
				classController: _ExampleController
			});
		},

		/**
		 * Hide header node as it is now rendered in React
		 */
		_addTaskSetupTitle() {
			html.hide(this.headerNode);
		},

		/**
		 * Return false if task in context references a view that is not possible to view without real data (root node)
		 * @param {Context} ctx
		 * @return {boolean} 
		 */
		_canSeeExampleView: function(ctx) {
			const task = ctx.getNode();
			const viewKey = task.ViewStoreId;

			if (!viewKey) {
				return false;
			}

			const viewPath = parseExtendedPath(task.ViewDefinitionPath);
			const defView = ViewStoreBuilder.getViewDefinition(viewKey, viewPath);

			return (defView.getType() != "DataSheet") &&
				(defView.getId() != "TDSSwitchboard") &&
				this._taskSupportsCustomViews(ctx);
		},

		/**
		 * Render example action
		 * @param {Context} ctx
		 * @param {DOMNode} div
		 * @param {boolean} [testForCustomViews] - if true, then only render if custom views are allowed on view
		 */
		_renderTaskSetupExampleAction: function(ctx, div, testForCustomViews) {
			if (!testForCustomViews || this._canSeeExampleView(ctx)) {
				this.getAction(ACTION_EXAMPLE).toHtmlWithAccess(ctx, html.create("div", null, div));
			}
		},

		/**
		 * Return true if task in context references view that supports custom views
		 * @param {Context}
		 * @return {boolean}
		 */
		_taskSupportsCustomViews: function(ctx) {
			const task = ctx.getNode();
			const processRevision = ctx.getNodeParent();
			const processDefinition = ctx.getNodeParent(processRevision);
			const viewKey = task.ViewStoreId;
			const viewPath = parseExtendedPath(task.ViewDefinitionPath);
			return !!ViewStoreBuilder.getCustomViewContainerKey(viewKey, viewPath, processDefinition.PrimarySubtype);
		}
	});
});